import { useId, useState } from 'react'
import { Wordmark } from './Wordmark'
import { useAutoFocus } from '../hooks/useAutoFocus'

interface Props {
  /** The account's own first name when it has one (lib/userName.ts); still editable. */
  suggestedName?: string
  onCreate: (name: string, displayName: string) => void
  onJoin: (code: string, displayName: string) => void
  loading: boolean
  error: string | null
}

export function Onboarding({ suggestedName = '', onCreate, onJoin, loading, error }: Props) {
  const [tab, setTab] = useState<'create' | 'join'>('create')
  const [householdName, setHouseholdName] = useState('')
  const [displayName, setDisplayName] = useState(suggestedName)
  const [inviteCode, setInviteCode] = useState('')

  function submit() {
    if (!displayName.trim()) return
    if (tab === 'create' && householdName.trim()) {
      onCreate(householdName.trim(), displayName.trim())
    } else if (tab === 'join' && inviteCode.trim()) {
      onJoin(inviteCode.trim(), displayName.trim())
    }
  }

  return (
    <div style={{
      minHeight: '100dvh', display: 'flex', flexDirection: 'column',
      alignItems: 'center', justifyContent: 'center',
      background: 'var(--ml-bg)', padding: '24px',
    }}>
      <div style={{ width: '100%', maxWidth: 390 }}>
        {/* Title */}
        <div style={{ textAlign: 'center', marginBottom: 40 }}>
          <h1>
            <Wordmark size={46} />
          </h1>
          <p style={{ color: 'var(--ml-text-secondary)', fontSize: 15, marginTop: 6 }}>
            One list, the whole family.
          </p>
        </div>

        {/* Tab switcher */}
        <div className="ml-segmented" style={{ marginBottom: 24 }}>
          {(['create', 'join'] as const).map(t => (
            <button key={t} type="button" aria-pressed={tab === t} onClick={() => setTab(t)}
              className={`ml-segmented__opt${tab === t ? ' is-selected' : ''}`}>
              {t === 'create' ? 'New household' : 'Join household'}
            </button>
          ))}
        </div>

        {/* A real form, so the phone fills both names at once and Enter sends it. */}
        <form noValidate onSubmit={e => { e.preventDefault(); submit() }}>
        <div style={{
          background: 'var(--ml-surface)', borderRadius: 'var(--ml-radius-input)',
          overflow: 'hidden', marginBottom: 16,
        }}>
          {tab === 'create' && (
            <Field
              label="Household name" value={householdName}
              onChange={setHouseholdName} placeholder="The Smiths"
              name="family-name" autoComplete="family-name"
              autoFocus border
            />
          )}
          {tab === 'join' && (
            <Field
              label="Invite code" value={inviteCode}
              onChange={v => setInviteCode(v.toUpperCase())}
              placeholder="AB1C2D" autoFocus border
              inputMode="text"
            />
          )}
          {/* given-name: the phone fills it from the owner's card (iPhone) or Google account (Android). */}
          <Field
            label="Your name" value={displayName}
            onChange={setDisplayName} placeholder="Alex"
            name="given-name" autoComplete="given-name"
          />
        </div>

        {error && (
          <p style={{ color: 'var(--ml-destructive)', fontSize: 13, marginBottom: 12, textAlign: 'center' }}>
            {error}
          </p>
        )}

        <button
          type="submit"
          disabled={loading || !displayName.trim() || (tab === 'create' ? !householdName.trim() : !inviteCode.trim())}
          style={{
            width: '100%', padding: '15px', border: 'none', borderRadius: 'var(--ml-radius-input)',
            background: 'var(--ml-primary-bg)', color: 'var(--ml-primary-fg)', fontSize: 16, fontWeight: 600,
            cursor: loading ? 'not-allowed' : 'pointer',
            opacity: loading ? 0.6 : 1,
            transition: 'opacity 0.15s',
          }}
        >
          {loading ? 'Setting up…' : tab === 'create' ? 'Create list' : 'Join list'}
        </button>
        </form>
      </div>
    </div>
  )
}

function Field({ label, value, onChange, placeholder, border, autoFocus, inputMode, name, autoComplete }: {
  label: string; value: string; onChange: (v: string) => void
  placeholder: string; border?: boolean; autoFocus?: boolean
  inputMode?: React.HTMLAttributes<HTMLInputElement>['inputMode']
  name?: string; autoComplete?: string
}) {
  const focusRef = useAutoFocus<HTMLInputElement>(autoFocus)
  const id = useId()
  return (
    <div style={{
      display: 'flex', alignItems: 'center', padding: '12px 16px',
      borderBottom: border ? '0.5px solid var(--ml-separator)' : 'none',
    }}>
      <label htmlFor={id} style={{ fontSize: 15, color: 'var(--ml-text-secondary)', width: 130, flexShrink: 0 }}>
        {label}
      </label>
      <input
        id={id} name={name} autoComplete={autoComplete ?? 'off'}
        value={value} onChange={e => onChange(e.target.value)}
        placeholder={placeholder} ref={focusRef}
        inputMode={inputMode}
        style={{
          flex: 1, border: 'none', outline: 'none', background: 'transparent',
          fontSize: 15, color: 'var(--ml-text)',
        }}
      />
    </div>
  )
}
